import { FC } from "react";
import { Divider, Group, Stack, Text } from "@mantine/core";
import BookitupDocument, { PartialInvoice } from "../../types/BookitupDocument";
import { FormatS } from "../../Service/FormatS";
import DocumentFinalSummary from "./DocumentFinalSummary";
import { Profile } from "../../types/Profile";
import {
  getDocumentTranslator,
  translateGeneratedDocumentText,
} from "./documentLanguage";
import { useLingui } from "@lingui/react";

interface Props {
  bookitupDocument: BookitupDocument;
  profile: Profile;
}

const DocumentPartialInvoices: FC<Props> = ({ bookitupDocument, profile }) => {
  const { partialInvoices, currency } = bookitupDocument;
  const { smallScaleBusiness } = profile;
  const { i18n } = useLingui();
  const t = getDocumentTranslator(i18n, bookitupDocument.language);

  if (!partialInvoices || partialInvoices.length === 0) {
    return null;
  }

  const mapPartialInvoiceUI = (partialInvoice: PartialInvoice) => {
    const { label, net, gross, vat } = partialInvoice;
    const translatedLabel = translateGeneratedDocumentText(label, t);
    return (
      <>
        <Text weight="bolder">{translatedLabel}</Text>
        {!smallScaleBusiness && (
          <>
            <Group position="apart">
              <Text>{t("net")}:</Text>
              <Text>
                {FormatS.formatPriceWithCurrency(currency, net)}
              </Text>
            </Group>
            <Group position="apart">
              <Text>{t("vat")}:</Text>
              <Text>
                {FormatS.formatPriceWithCurrency(currency, vat)}
              </Text>
            </Group>
          </>
        )}
        <Group position="apart">
          <Text>{smallScaleBusiness ? t("totalAmount") : t("gross")}:</Text>
          <Text>
            - {FormatS.formatPriceWithCurrency(currency, gross)}
          </Text>
        </Group>
      </>
    );
  };

  return (
    <>
      <Divider mb="md" />
      <Stack spacing={5}>{partialInvoices.map(mapPartialInvoiceUI)}</Stack>
      <Divider mt="md" />
      <DocumentFinalSummary
        bookitupDocument={bookitupDocument}
        smallScaleBusiness={smallScaleBusiness}
        t={t}
      />
    </>
  );
};

export default DocumentPartialInvoices;
